import {
    getConfig,
    AppConfig,
} from '@superbet-group/web.lib.config';
import {
    Locale,
    TransactionType,
} from '@superbet-group/web.types';
import {
    createCTRestClient,
    CTResponseData,
    processResponse,
    RestClient,
} from '@superbet-group/web.lib.rest';
import { IResponse } from '@superbet-group/web.lib.utilities';
import { getClientSourceParam } from '@superbet-group/web.lib.vue-utilities';
import { featureFlagService } from '@superbet-group/web.lib.flags';

import { defaultHttpTimeoutMs } from '@core/constants';
import { BalanceDto } from '@core/mappers/user/dtos/BalanceDto';
import IHistoryDto from '@core/mappers/user/dtos/IHistoryDto';
import { WalletDto } from '@core/mappers/user/dtos/WalletDto';
import {
    BankWithdrawalResponseDto,
    OnlineWithdrawalResponseDto,
    ShopWithdrawalResponseDto,
} from '@core/mappers/user/dtos/WithdrawalResponseDto';
import {
    AccountData,
    WithdrawalAbilityDto,
} from '@core/models/user/withdrawal/WithdrawalAbility';
import { getCsrfToken } from '@core/services/authentication/helpers';
import { AssembledMarketingAttributionData } from '@core/services/marketing/_private/types';
import {
    PlayerDetailsFieldType,
    WithdrawalType,
} from '@models/user/enums';
import History from '@models/user/history/History';
import WithdrawalRequest from '@models/user/withdrawal/WithdrawalRequest';
import { shellLocalConfig } from '@src/config/shell/localConfig';

import {
    IdentityDataPayload,
    PrivacyPolicyData,
    RegistrationSecondStepParams,
    LicencePreferencesPayload,
    LicencePreferencesResponse,
    GetPlayerDetailsResponse,
    VerificationConfigResponse,
    SerProPayload,
    SerProResponse,
    LoginHistoryResponse,
    FaceVerificationRequestParams,
    SecurityCheckAction,
    VerificationRequestParams,
} from './Types';
import { maybeGetMarketingData } from '@core/services/marketing/marketing-data';
import {
    BankAccount,
    RegisterBankAccountRequest,
    UnregisterBankAccountRequest,
} from '@core/models/user/Wallet';

const ms = require('ms');
const uploadDocumentTimeout = ms('5m');

export default class RestService {
    private readonly endpoints = this.config.services.playerAccountManagement;
    private readonly restClient: RestClient;

    constructor(private config: AppConfig) {
        this.restClient = createCTRestClient(
            this.config.user.host,
            defaultHttpTimeoutMs,
            getClientSourceParam(),
            true,
            getCsrfToken,
        );
    }

    /**
     * @throws {RequestError}
     */
    public getRegisteredBanks(): Promise<BankAccount[]> {
        return this.restClient.get(this.endpoints.bankRegistrationUrl!);
    }

    /**
     * @throws {RequestError}
     */
    public registerBankAccount(registerBankAccountRequest: RegisterBankAccountRequest): Promise<any> {
        const endpoint = this.endpoints.bankRegistrationUrl;
        return this.restClient.post(
            endpoint!,
            registerBankAccountRequest,
        );
    }

    /**
     * @throws {RequestError}
     */
    public unregisterBankAccount(unregisterBankAccountRequest: UnregisterBankAccountRequest): Promise<any> {
        const endpoint = this.endpoints.bankRegistrationUrl;
        return this.restClient.post(
            endpoint!,
            unregisterBankAccountRequest,
        );
    }

    /**
     * @throws {RequestError}
     */
    public uploadIdCard(file: string, fileName: string, username?: string, otc?: string): Promise<any> {
        // readAsDataURL adds a prefix which is used for thumbnails. It has to be removed from the actual image
        // https://developer.mozilla.org/en-US/docs/Web/API/FileReader/readAsDataURL
        const base64Content = file.substr(file.indexOf(',') + 1);
        const uploadEndpoint = username ? this.endpoints.uploadReactivationDocument : this.endpoints.uploadDocumentVerification;
        return this.restClient.post(
            uploadEndpoint!,
            {
                fileName,
                file: base64Content,
                username,
                otc,
            },
            {
                timeout: uploadDocumentTimeout,
            },
        );
    }

    /**
     * @throws {RequestError}
     */
    public registrationSecondStep(file: string, fileName: string, params: RegistrationSecondStepParams): Promise<any> {
        // readAsDataURL adds a prefix which is used for thumbnails. It has to be removed from the actual image
        // https://developer.mozilla.org/en-US/docs/Web/API/FileReader/readAsDataURL
        const base64Content = file.substr(file.indexOf(',') + 1);
        return this.restClient.post(
            this.endpoints.registrationSecondStep,
            {
                fileName,
                file: base64Content,
                ...params,
            },
            {
                timeout: uploadDocumentTimeout,
            },
        );
    }

    /**
     * @throws {RequestError}
    */

    public updateUsername(username: string): Promise<any> {
        return this.restClient.post(
            this.endpoints.update,
            {
                username,
            },
            {
                headers: {
                    // eslint-disable-next-line sonarjs/no-duplicate-string
                    'Content-Type': 'application/json',
                },
            },
        );
    }

    public updateBrasilMigrationDetails(gender: string, nationality: string): Promise<any> {
        return this.restClient.post(
            this.endpoints.update,
            {
                gender,
                nationality,
            },
            {
                headers: {
                    'Content-Type': 'application/json',
                },
            },
        );
    }

    /**
     * @throws {RequestError}
     */
    public updatePhone(phone: string): Promise<any> {
        return this.restClient.post(
            this.endpoints.update,
            {
                phone,
            },
            {
                headers: {
                    'Content-Type': 'application/json',
                },
            },
        );
    }

    public setLocale(locale: Locale): Promise<IResponse<void>> {
        return processResponse(this.restClient.post(
            (this.endpoints as any).setLocale,
            {
                locale: locale.toString(),
            },
            {
                headers: {
                    'Content-Type': 'application/json',
                },
            },
        ));
    }

    /**
     * @throws {RequestError}
     */
    public updatePrivacy(data: PrivacyPolicyData): Promise<any> {
        return processResponse(this.restClient.post(
            this.endpoints.updatePrivacyConsent,
            data,
            {
                headers: {
                    'Content-Type': 'application/json',
                },
            },
        ));
    }

    /**
     * @throws {RequestError}
     */
    public getUserDetails(fields?: PlayerDetailsFieldType[]): Promise<GetPlayerDetailsResponse> {
        const params: Record<string, string> = fields && fields.length > 0
            ? { fields: fields.join(',') }
            : {};

        return this.restClient.get<GetPlayerDetailsResponse>(
            this.endpoints.getPlayerDetails,
            {
                params,
            },
        );
    }

    /**
 * @throws {RequestError}
     */
    public getPlayerSettings(settingName: string): Promise<any> {
        return processResponse(this.restClient.get(
            this.endpoints.getPlayerSettings,
            {
                params: {
                    settingName,
                },
            },
        ));
    }

    /**
     * @throws {RequestError}
     */
    public getPlayerBalance(): Promise<IResponse<BalanceDto>> {
        return processResponse(this.restClient.get(
            this.endpoints.getPlayerBalance,
            {
                params: {
                    IncludeBonusProductType: true,
                    IncludeExternalBalances: true,
                    IncludeBonusType: true,
                },
            },
        ));
    }

    /**
     * @throws {RequestError}
     */
    public getWalletBreakdown<Response extends { data: WalletDto }>(): Promise<Response> {
        return processResponse(this.restClient.get(this.endpoints.walletBreakdown));
    }

    /**
     * @throws {RequestError}
     */
    public async createDeposit(amount: number, paySafeCard: boolean, isPaymentIq: boolean, redirectPagePath = 'dwstatus', session?: string, sessionId?: string, isQuickDeposit?: boolean, provider?: string | null): Promise<any> {
        const marketingData = await maybeGetMarketingData();
        const integrationTest = await featureFlagService.getFeatureValue<string>('wallet.use-integration-test', '0');

        const data: any = {
            amount,
            Parameters: {
                SuccessURL: encodeURI(`https://${window.location.host}/${redirectPagePath}?transactionStatus=success`),
                FailureURL: encodeURI(`https://${window.location.host}/${redirectPagePath}?transactionStatus=failed`),
                PendingURL: encodeURI(`https://${window.location.host}/${redirectPagePath}?transactionStatus=pending`),
                BackURL: encodeURI(`https://${window.location.host}/${redirectPagePath}?transactionStatus=cancel`),
            },
            integration_test: integrationTest,
            ...(isPaymentIq && { PaymentProviderId: 5 }),
            ...(!isQuickDeposit && { psc: paySafeCard }),
            ...marketingData,
            ...provider && { provider },
            cashierVersion: 'web',
        };
        let endpoint = this.endpoints.createOnlineDeposit;

        if (shellLocalConfig.seonConfig.transactions.shouldSendWithTransaction && session && sessionId) {
            data.session = session;
            data.sessionId = sessionId;
        }

        if (isQuickDeposit) {
            endpoint = this.endpoints.createQuickDeposit!;
        }

        return processResponse(this.restClient.post(endpoint, data) as Promise<CTResponseData<unknown>>);
    }

    /**
     * @throws {RequestError}
     */
    public createTransaction(amount: number, targetId: string, action: TransactionType, session?: string, sessionId?: string): Promise<any> {
        function getTransactionProperties(action: TransactionType) {
            if (action === TransactionType.Deposit) {
                return {
                    e_money_pin: targetId,
                    action: 'deposit',
                };
            } else {
                return {
                    destination_account: targetId,
                    action: 'withdraw',
                };
            }
        }

        const data: any = {
            amount,
            provider: 'transaks',
            cashierVersion: 'web',
            ...getTransactionProperties(action),
        };

        let endpoint = this.endpoints.createOnlineTransaction;

        if (shellLocalConfig.seonConfig.transactions.shouldSendWithTransaction && session && sessionId) {
            data.session = session;
            data.sessionId = sessionId;
        }

        return processResponse(this.restClient.post(
            endpoint,
            data,
        ));
    }

    /**
    * @throws {RequestError}.
    */
    public setPlayerSettings(settingName: string, settingValue: Record<string, unknown>| number): Promise<any> {
        return processResponse(this.restClient.post(
            this.endpoints.updatePlayerSettings,
            {
                settingName,
                settingValue,
            },
        ));
    }

    /**
     * @throws {RequestError}
     */
    public async createOnlineWithdrawal(withdrawalRequest: WithdrawalRequest, redirectPagePath: string, session?: string, sessionId?: string, otp?: string): Promise<IResponse<OnlineWithdrawalResponseDto>> {
        const integrationTest = await featureFlagService.getFeatureValue<string>('wallet.use-integration-test', '0');
        const data: any = {
            amount: withdrawalRequest.amount,
            paymentInstrumentType: withdrawalRequest.paymentInstrumentType,
            psc: withdrawalRequest.psc,
            action: withdrawalRequest.action,
            integration_test: integrationTest,
            ...(withdrawalRequest.customerPaymentDeviceId && { customerPaymentDeviceId: withdrawalRequest.customerPaymentDeviceId }),
            ...(withdrawalRequest.PaymentProviderId && { PaymentProviderId: withdrawalRequest.PaymentProviderId }),
            Parameters: {
                SuccessURL: encodeURI(`https://${window.location.host}/${redirectPagePath}?transactionStatus=success`),
                FailureURL: encodeURI(`https://${window.location.host}/${redirectPagePath}?transactionStatus=failed`),
                PendingURL: encodeURI(`https://${window.location.host}/${redirectPagePath}?transactionStatus=pending`),
                BackURL: encodeURI(`https://${window.location.host}/${redirectPagePath}?transactionStatus=cancel`),
            },
        };

        if (withdrawalRequest.psc) {
            data.dateOfBirth = withdrawalRequest.dateOfBirth;
            data.firstName = withdrawalRequest.firstName;
            data.lastName = withdrawalRequest.lastName;
            data.email = withdrawalRequest.email;
        }

        if (shellLocalConfig.seonConfig.transactions.shouldSendWithTransaction && session && sessionId) {
            data.session = session;
            data.sessionId = sessionId;
        }

        if (otp) {
            data.otp = otp;
        }

        return processResponse(this.restClient.post(
            this.endpoints.createOnlineWithdrawal,
            data,
        ));
    }

    public createBankTransferWithdrawal(withdrawalRequest: WithdrawalRequest, session?: string, sessionId?: string): Promise<IResponse<BankWithdrawalResponseDto>> {
        return processResponse(this.restClient.post(
            this.endpoints.createBankTransferWithdrawal,
            {
                amount: withdrawalRequest.amount,
                paymentInstrumentType: withdrawalRequest.paymentInstrumentType,
                ...(withdrawalRequest[AccountData.AccountReference] && withdrawalRequest[AccountData.HolderName] && {
                    [AccountData.AccountReference]: withdrawalRequest[AccountData.AccountReference],
                    [AccountData.HolderName]: withdrawalRequest[AccountData.HolderName],
                }),
                ...(shellLocalConfig.seonConfig.transactions.shouldSendWithTransaction && session && sessionId && {
                    session,
                    sessionId,
                }),
                cashierVersion: 'web',
            },
        ));
    }

    /**
     * @throws {RequestError}
     */
    public checkDepositEligibility(): Promise<any> {
        return processResponse(this.restClient.get(this.endpoints.checkDepositEligibility));
    }

    public checkWithdrawalEligibility(): Promise<any> {
        return processResponse<IResponse<WithdrawalAbilityDto>>(this.restClient.get(this.endpoints.checkWithdrawalEligibility));
    }

    /**
     * @throws {RequestError}
     */
    public createShopWithdrawal(withdrawalRequest: WithdrawalRequest, session?: string, sessionId?: string): Promise<IResponse<ShopWithdrawalResponseDto>> {
        return processResponse(this.restClient.post(
            this.endpoints.shopWithdrawal,
            {
                amount: withdrawalRequest.amount,
                shopId: withdrawalRequest.shopId,
                withdrawalType: withdrawalRequest.type === WithdrawalType.Shop ? 'REGULAR' : 'INSTANT',
                ...(shellLocalConfig.seonConfig.transactions.shouldSendWithTransaction && session && sessionId && {
                    session,
                    sessionId,
                }),
            },
        ));
    }

    /**
     * @throws {RequestError}
     */
    public fetchTransactions(page: number, pageSize: number): Promise<{ data: IHistoryDto[] }> {
        // weird logic with start and limit on leapbit's part
        const start = page * pageSize;
        return processResponse(this.restClient.get(
            this.endpoints.getPlayerTransactions,
            {
                params: {
                    start,
                    limit: pageSize,
                },
            },
        ));
    }

    /**
     * @throws {RequestError}
     */
    public fetchAllTransactions(page: number, pageSize: number): Promise<{ data: History[] }> {
        const start = page * pageSize;
        return processResponse(this.restClient.get(
            this.endpoints.getAllPlayerTransactions,
            {
                params: {
                    start,
                    limit: pageSize,
                },
            },
        ));
    }

    /**
     * @throws {RequestError}
     */
    public fetchAllHistoryTransactions(): Promise<any> {
        return processResponse(this.restClient.get(this.endpoints.getAllHistoryPlayerTransactions));
    }

    /**
     * @throws {RequestError}
     */
    public fetchCasinoPlays(page: number, pageSize: number): Promise<{ data: IHistoryDto[] }> {
        // weird logic with start and limit on leapbit's part
        const start = page * pageSize;
        return processResponse(this.restClient.get(
            this.endpoints.getGameHistory,
            {
                params: {
                    start,
                    limit: pageSize,
                },
            },
        ));
    }

    /**
     * @throws {RequestError}
     */
    public fetchVirtualsPlays(page: number, pageSize: number): Promise<{ data: IHistoryDto[] }> {
        // weird logic with start and limit on leapbit's part
        const start = page * pageSize;
        if (!this.endpoints.getVirtualGamesHistory) {
            return Promise.reject(new Error('Virtual games history url is not defined'));
        }
        return processResponse(this.restClient.get(
            this.endpoints.getVirtualGamesHistory,
            {
                params: {
                    start,
                    limit: pageSize,
                },
            },
        ));
    }

    /**
     * @throws {RequestError}
     */
    public fetchBets(
        page: number,
        pageSize = getConfig().app.user.history.pageSize,
    ): Promise<{ data: IHistoryDto[] }> {
        // weird logic with start and limit on leapbit's part
        const start = page * pageSize;
        return processResponse(this.restClient.get(
            this.endpoints.getBetsHistory,
            {
                params: {
                    start,
                    limit: pageSize,
                },
            },
        ));
    }

    /**
     * @throws {RequestError}
     */
    public fetchMessages(): Promise<any> {
        return processResponse(this.restClient.get(this.endpoints.getPopupMessages));
    }

    /**
     * @throws {RequestError}
     */
    public respondToMessage(actionId: number): Promise<any> {
        return this.restClient.post(
            this.endpoints.messageAction,
            {
                actionId,
                fetchMessages: false,
            },
        );
    }

    /**
     * @throws {RequestError}
     */
    public cancelWithdrawal(history: History): Promise<any> {
        return processResponse(this.restClient.post(
            this.endpoints.cancelWithdrawal,
            {
                transactionId: history.id,
            },
        ));
    }

    /**
     * @throws {RequestError}
     */
    public sendSmsCode(): Promise<any> {
        return this.restClient.post(this.endpoints.resendSmsCode);
    }

    /**
     * @throws {RequestError}
     */
    public validateSmsCode(code: string): Promise<any> {
        return this.restClient.post(
            this.endpoints.validateSmsCode,
            {
                smsCode: code,
            },
        );
    }

    public getLottoFavorites(): Promise<any> {
        return processResponse(this.restClient.get(this.endpoints.getLottoFavorites));
    }

    public setLottoFavorites(data: any): Promise<any> {
        return processResponse(this.restClient.post(
            this.endpoints.setLottoFavorites,
            {
                favs: data,
            },
        ));
    }

    public sendActivationEmail(emailAddress: string): Promise<any> {
        return this.restClient.post(
            this.endpoints.sendActivationEmail,
            {
                emailAddress,
            },
        );
    }

    /**
     * @throws {RequestError}
     */
    public updateCustomParams(data: AssembledMarketingAttributionData): Promise<any> {
        return processResponse(this.restClient.post(
            this.endpoints.updateCustomParams,
            data,
            {
                headers: {
                    'Content-Type': 'application/json',
                },
            },
        ));
    }

    public sendIdentityData(data: IdentityDataPayload): Promise<any> {
        return this.restClient.post(
            this.endpoints.identityData || '#',
            data,
        );
    }

    public getUserLicencePreferences(): Promise<LicencePreferencesResponse> {
        return this.restClient.get(
            this.endpoints.playerLicencePreferences,
        );
    }

    public updateUserLicencePreferences(data: LicencePreferencesPayload): Promise<{ error: boolean }> {
        return this.restClient.post(
            this.endpoints.playerLicencePreferences,
            data,
        );
    }

    public async getIdentityVerificationRequest(payload: VerificationRequestParams): Promise<VerificationConfigResponse> {
        const useV2Url = await featureFlagService.getFeatureValue('pam.verificationUrlV2Enabled', true);
        const endpoint = useV2Url ? this.endpoints.getVerificationUrl.replace('v1', 'v2') : this.endpoints.getVerificationUrl;

        const data = {
            params: {
                action: payload.action || SecurityCheckAction.identify,
                ...(payload.action === SecurityCheckAction.faceid && { flow: payload.flow }),
            },
        };
        return this.restClient.get(
            endpoint,
            data,
        );
    }

    public async getAnonymousIdentityVerificationRequest(params: FaceVerificationRequestParams): Promise<VerificationConfigResponse> {
        const useV2Url = await featureFlagService.getFeatureValue('pam.verificationUrlV2Enabled', true);
        const endpoint = useV2Url ? this.endpoints.getFaceVerificationUrl!.replace('v1', 'v2') : this.endpoints.getFaceVerificationUrl!;

        return this.restClient.get(
            endpoint,
            {
                params,
            },
        );
    }

    public getRegulatorData(payload: SerProPayload): Promise<SerProResponse> {
        return this.restClient.post(
            this.endpoints.getRegulatorData,
            payload,
        );
    }

    public getLoginHistory(): Promise<LoginHistoryResponse> {
        return this.restClient.get(
            this.endpoints.getLoginHistory!,
        );
    }
}
